import { Flex } from 'antd';
import { useRef, type PointerEvent } from 'react';
import type { Position } from 'business/receipt/pages/choose-segment-status/types';

interface ImagePointerProps {
  position?: Position;
  image: string;
  setPosition?: (value: Position) => void;
}

export const ImagePointer = ({
  position = { x: 0, y: 0 },
  image,
  setPosition,
}: ImagePointerProps) => {
  const imageRef = useRef<HTMLImageElement>(null);

  const calculatePosition = (event: PointerEvent<HTMLImageElement>) => {
    const imageElement = imageRef.current;
    if (!imageElement) {
      return null;
    }

    // retrieve position and size of the viewport
    const rect = imageElement.getBoundingClientRect();

    // calculate event position relative to the viewport
    // we can use event.touches[0].clientX for touch event
    const xPixels = event.clientX - rect.left;
    const yPixels = event.clientY - rect.top;

    // we use a percentage in order to display the position whatever the size of the image
    const xPercent = (xPixels / rect.width) * 100;
    const yPercent = (yPixels / rect.height) * 100;

    // final value should be between 0 and 100
    const clampedX = Math.min(100, Math.max(0, xPercent));
    const clampedY = Math.min(100, Math.max(0, yPercent));

    return {
      x: parseFloat(clampedX.toFixed(2)),
      y: parseFloat(clampedY.toFixed(2)),
    };
  };

  const handlePointerDown = (event: PointerEvent<HTMLImageElement>) => {
    event.preventDefault();

    // we only want to display the position of the defect
    if (!setPosition) {
      return;
    }

    const newPosition = calculatePosition(event);
    if (newPosition) {
      setPosition(newPosition);
    }
  };

  return (
    <Flex justify="center">
      <div className="relative h-18.75 w-37.5 touch-none select-none">
        <img
          ref={imageRef}
          src={image}
          className="block h-full w-full"
          onPointerDown={handlePointerDown}
          onTouchMove={(e) => e.preventDefault()}
        />
        <div
          className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-red-500"
          style={{
            left: `${position.x}%`,
            top: `${position.y}%`,
          }}
        />
      </div>
    </Flex>
  );
};
